import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin, DatePicker } from 'antd';
import { errHandler } from '../../helper/helper';
import moment from 'moment';
import { disabledDatesBeforeYear } from "../../Utils/Util";
import { generateBlob } from "../../Utils/CreateBlob";
import { generateActiveAndDormantWasamWise } from "./LoanService";
import BranchLocationSelector from '../BranchLocationSelector';

const { Option } = Select;

const LoanTrailBalance = ({
    loginPlaceId,
    products,
    districtData,
    districtName,
    branchId,
    divitionalAreaId,
    wasamData
}) => {

    const [selectedDistricts, setSelectedDistricts] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedDivisionalAreas, setSelectedDivisionalAreas] = useState([]);
    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loanTrailBalanceDate, setLoanTrailBalanceDate] = useState(null);
    const [selectedWasam, setSelectedWasam] = useState([]);
    const [selectedScope, setSelectedScope] = useState();
    const [locationData, setLocationData] = useState({});

    // Update divisional areas based on districts or divisional-level user
    useEffect(() => {
        let filteredDivisionalAreas = [];

        if (loginPlaceId === "2" && divitionalAreaId) {
            filteredDivisionalAreas = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => div.divAId === divitionalAreaId);

            setSelectedDivisionalAreas([divitionalAreaId]);
        } else {
            filteredDivisionalAreas = districtData
                .filter(d => selectedDistricts.includes(d.districtId))
                .flatMap(d => d.divisionalAreaDtoList || []);

            setSelectedDivisionalAreas(prev =>
                prev.filter(did =>
                    filteredDivisionalAreas.some(d => d.divAId === did)
                )
            );
        }

        setAvailableDivisionalAreas(filteredDivisionalAreas);
    }, [selectedDistricts, districtData, loginPlaceId, divitionalAreaId]);

    // Update available branches based on selected districts
    useEffect(() => {
        const filtered = districtData
            .filter(d => selectedDistricts.includes(d.districtId))
            .flatMap(d => d.branchDtoList || []);
        setAvailableBranches(filtered);

        setSelectedBranches(prev => prev.filter(bid =>
            filtered.some(b => b.id === bid)
        ));
    }, [selectedDistricts, districtData]);

    // Update available branches based on selected divisional areas
    useEffect(() => {
        const filteredBranches = districtData
            .flatMap(d => d.divisionalAreaDtoList || [])
            .filter(div => selectedDivisionalAreas.includes(div.divAId))
            .flatMap(div => div.branchDtoList || []);

        setAvailableBranches(filteredBranches);

        setSelectedBranches(prev =>
            prev.filter(bid =>
                filteredBranches.some(b => b.id === bid)
            )
        );
    }, [selectedDivisionalAreas, districtData]);

    const reportTypeConfig = {
        branchWise: {
            label: 'Branch Wise',
            key: 0,
            disableWasam: true
        },
        gnWise: {
            label: 'Wasam Wise',
            key: 1,
            disableWasam: false
        },
    };

    const [selectedReportType, setSelectedReportType] = useState('branchWise');

    const handleReportTypeChange = (value) => {
        setSelectedReportType(value);
    };


    // Generate report
    const generateReport = async () => {
        let data = {
            wasamIdList: loginPlaceId === "1" ? selectedWasam : [],
            branchId: loginPlaceId === "1" ? branchId : locationData.branchId,
            productIdList: selectedProducts,
            activeStatus: selectedScope,
            selectedDate: loanTrailBalanceDate,
            selectionType: reportTypeConfig[selectedReportType]?.key,
        };
        setLoading(true);
        generateActiveAndDormantWasamWise(data).then((res) => {
            generateBlob(res.data)
        }).catch(err => {
            errHandler(err)
        }).finally(() => {
            setLoading(false);
        });
    };

    const activeStatus = [
        { value: 0, label: "Active Loans" },
        { value: 1, label: "Non Performing Loans" }
    ];

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]}>
                        <BranchLocationSelector loginPlaceId={loginPlaceId} districtData={districtData} districtName={districtName} onChange={(setLocationData)}/>
                        <Col span={24}>
                            <Form.Item label="Active">
                                <Select
                                    style={{ width: '100%' }}
                                    onChange={setSelectedScope}
                                    options={activeStatus}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    {loginPlaceId === "1" && (
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item label="Select Report Type">
                                    <Select
                                        onChange={handleReportTypeChange}
                                        value={selectedReportType}
                                        style={{ width: '100%' }}
                                    >
                                        {Object.entries(reportTypeConfig).map(([key, config]) => (
                                            <Option key={key} value={key}>
                                                {config.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                            </Col>
                        </Row>
                    )}

                    {loginPlaceId === "1" && (
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item label="Wasam">
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        placeholder="Select Wasam(s)"
                                        value={selectedWasam}
                                        onChange={(values) => {
                                            if (values.includes('ALL')) {
                                                setSelectedWasam(wasamData.map(w => w.wastId));
                                            } else {
                                                setSelectedWasam(values);
                                            }
                                        }}
                                        style={{ width: '100%' }}
                                        disabled={reportTypeConfig[selectedReportType]?.disableWasam}  // 🔥 added here

                                    >
                                        <Option key="ALL" value="ALL">🔘 Select All Wasam</Option>
                                        {wasamData.map((wasam) => (
                                            <Option key={wasam.wastId} value={wasam.wastId}>
                                                {wasam.wasName}-{wasam.wasName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}                    

                    <Col span={24}>
                        <Form.Item label="Select Products">
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Select products"
                                style={{ width: '100%' }}
                                value={selectedProducts}
                                onChange={(values) => {
                                    if (values.includes('ALL')) {
                                        setSelectedProducts(products.map(b => b.productId));
                                    } else {
                                        setSelectedProducts(values);
                                    }
                                }}
                            >
                                <Option key="ALL" value="ALL">
                                    🔘 Select All Products
                                </Option>
                                {products.map(product => (
                                    <Option key={product.productId} value={product.productId}>
                                        {product.productName}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Date">
                                <DatePicker onChange={(date) => setLoanTrailBalanceDate(date)} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    onClick={generateReport}
                                    disabled={loading}
                                    className="generate-btn"
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default LoanTrailBalance;
